import { ChangeEvent, useRef, useState } from 'react';
import {
  updateTenantPhone,
  verifyVerificationCodePhone,
  verifyVerificationCodePhoneUpdate,
} from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { CANANA_COUNTRY_CODE } from '../../../constants/canadaCountryCode';
import { formatPhoneNumber } from '../../utils/utils';

type VerifyPhoneCodeProps = {
  phoneNumber: string;
  mode?: 'update' | 'verify';
  embedded?: boolean;
  onSuccess: () => void;
};

const VerifyPhoneCode = ({
  phoneNumber,
  mode = 'verify',
  embedded = false,
  onSuccess,
}: VerifyPhoneCodeProps) => {
  const { auth } = useAuth();

  const [errorMessage, setErrorMessage] = useState('');
  const [validMessage, setValidMessage] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];
  const [inputValues, setInputValues] = useState(['', '', '', '']);

  const handleInputChange = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.slice(0, 1);
    if (!/^\d$/.test(value)) {
      value = '';
    }
    const newValues = [...inputValues];
    newValues[index] = value;
    setInputValues(newValues);
    if (value.length === 1 && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleSuccess = () => {
    setValidMessage(
      mode === 'update'
        ? 'Your phone has been verified and updated'
        : 'Your phone number has been verified'
    );
    setIsVerified(true);
    setTimeout(() => onSuccess(), 1500);
  };

  const handleVerify = async () => {
    const code = inputValues.join('');
    const isValidCode = code.length === 4 && /^\d{4}$/.test(code);
    if (!isValidCode) {
      setErrorMessage('Please enter a valid code');
      return;
    }

    setErrorMessage('');
    setLoadingBtn(true);
    try {
      const token = auth?.data.token || '';
      setLoading(true);

      if (mode === 'verify') {
        const response = await verifyVerificationCodePhone(
          `+${CANANA_COUNTRY_CODE}${phoneNumber}`,
          token,
          code
        );
        if (
          response.message === 'verification_successful' ||
          response.message === 'phone_number_already_verified'
        ) {
          handleSuccess();
        } else {
          setErrorMessage('Invalid verification code');
        }
        return;
      }

      const response = await verifyVerificationCodePhoneUpdate(
        `+${CANANA_COUNTRY_CODE}${phoneNumber}`,
        token,
        code
      );

      switch (response.message) {
        case 'verification_success': {
          const updateResponse = await updateTenantPhone(auth?.data.token, {
            userDetails: { phoneNumber: phoneNumber },
          });

          if (updateResponse.message === 'updated') {
            handleSuccess();
          }
          break;
        }

        case 'invalid_verification_code':
          setErrorMessage('Invalid verification code');
          break;

        case 'phone_number_does_not_exist':
          setErrorMessage('Phone number does not exist');
          break;

        case 'verification_code_expired':
          setErrorMessage('Verification code has expired');
          break;

        case 'verification_attempts_exceeded':
          setErrorMessage('Verification attempts exceeded');
          break;

        default:
          setErrorMessage('Verification failed');
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        console.log(error.message);
      }
      setErrorMessage('Error verifying code');
    } finally {
      setLoadingBtn(false);
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (['+', '-'].includes(e.key)) {
      e.preventDefault();
    } else if (e.key === 'Enter') {
      handleVerify();
    } else if (e.key === 'Backspace') {
      if (inputValues[index] === '' && index > 0) {
        inputRefs[index - 1].current?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('Text');
    const digits = pastedData.replace(/\D/g, '').substring(0, 4).split('');

    const newValues = [...inputValues];
    newValues[0] = digits[0] || '';
    newValues[1] = digits[1] || '';
    newValues[2] = digits[2] || '';
    newValues[3] = digits[3] || '';
    setInputValues(newValues);
    inputRefs[digits.length > 0 ? digits.length - 1 : 0].current?.focus();
  };

  const content = (
    <div className={embedded ? 'flex flex-col gap-6' : 'flex flex-col gap-10'}>
      <div className={embedded ? 'text-[14px] text-[#6B7285]' : 'text-main'}>
        Enter the verification code sent to{' '}
        <span className='text-primary font-semibold'>
          {formatPhoneNumber(`${phoneNumber}`, true)}
        </span>{' '}
        to continue.
      </div>
      <div className='flex flex-col gap-[30px]'>
        <div className='flex flex-col gap-[10px]'>
          <div className='w-[222px] mx-auto flex justify-between items-center'>
            {inputRefs.map((ref, index) => (
              <input
                key={index}
                type='number'
                className='size-[48px] flex items-center justify-center border-[#EBEDF0] border-[1px] px-[12px] rounded-[8px] outline-none text-[#7A8699]
    text-[14px]/[170%] text-center font-semibold'
                ref={ref}
                value={inputValues[index]}
                onChange={handleInputChange(index)}
                maxLength={1}
                min={0}
                max={9}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={handlePaste}
                onWheel={(e) => (e.target as HTMLInputElement).blur()}
              />
            ))}
          </div>
          {errorMessage && <div className='error'>{errorMessage}</div>}
          {validMessage && <div className='text-[#027A48] text-[13px]'>{validMessage}</div>}
        </div>
        <button
          className='submitBtn'
          onClick={handleVerify}
          disabled={isVerified || inputValues.some((val) => val === '') || loading}
        >
          {!loadingBtn && <span>Verify</span>}
          {loadingBtn && <span style={{ display: 'block' }}>Please wait...</span>}
        </button>
      </div>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <section className='flex items-center justify-center  relative overflow-hidden '>
      <main className='flex flex-col items-center justify-center gap-[40px] p-[60px] rounded-[24px] shadow-[0_1px_3px_0_rgba(0,0,0,0.1)] bg-[#fff] z-[1] relative'>
        {content}
      </main>
    </section>
  );
};

export default VerifyPhoneCode;
